import { Link, router } from '@inertiajs/react';
import { formatDistanceToNow } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { FeedbackRow, Pagination } from '@/types';

interface FeedbackTableProps {
  eventSlug: string;
  feedback: FeedbackRow[];
  pagination: Pagination;
}

export function FeedbackTable({
  eventSlug,
  feedback,
  pagination,
}: FeedbackTableProps) {
  const changePage = (newPage: number) => {
    const params = new URLSearchParams(window.location.search);
    params.set('page', newPage.toString());
    router.get(`/admin/${eventSlug}/feedback?${params.toString()}`);
  };

  if (feedback.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        <p>No feedback responses found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Hacker</TableHead>
              <TableHead>Project</TableHead>
              <TableHead>Team</TableHead>
              <TableHead className="text-center">Rating</TableHead>
              <TableHead className="text-center">NPS</TableHead>
              <TableHead className="text-center">Aura</TableHead>
              <TableHead>Uploaded</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {feedback.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="font-medium">
                  <div className="flex flex-col gap-0.5">
                    <span>{row.hackerFullName}</span>
                    <span className="text-muted-foreground text-xs">
                      {row.hackerEmail}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  {row.projectSlug && row.projectName ? (
                    <Link
                      href={`/projects/${row.projectSlug}`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {row.projectName}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground text-sm">
                      No project
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {row.teamId && row.teamSlug ? (
                    <Link
                      href={`/admin/${eventSlug}/teams/${row.teamSlug}`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {row.teamSlug}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground text-sm">
                      No team
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-center font-medium">
                  {row.overallRating}
                </TableCell>
                <TableCell className="text-center font-medium">
                  <span
                    className={
                      row.npsScore >= 9
                        ? 'text-green-600'
                        : row.npsScore <= 6
                          ? 'text-red-600'
                          : 'text-muted-foreground'
                    }
                  >
                    {row.npsScore}
                  </span>
                </TableCell>
                <TableCell className="text-center font-medium">
                  {row.auraRating ?? (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <div className="text-sm">
                      {new Date(row.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {formatDistanceToNow(new Date(row.createdAt), {
                        addSuffix: true,
                      })}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="outline" size="sm" asChild>
                    <Link href={`/admin/${eventSlug}/feedback/${row.id}`}>
                      View
                    </Link>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between px-4 py-4">
        <div className="text-muted-foreground text-sm">
          Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
          {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
          {pagination.total} responses
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => changePage(pagination.page - 1)}
            disabled={pagination.page === 1}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Previous
          </Button>
          <span className="text-muted-foreground text-sm">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => changePage(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages}
          >
            Next
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
